import { useEffect, useRef, useState } from "preact/hooks";
import { blueprint, cart, close, external, filePdf, fileText } from "../icons.ts";
import { area, floor, price, rooms, shopPrice } from "../lib/format.ts";
import { interceptLinks, openFile } from "../lib/links.ts";
import { isProduct, type Card, type ProductCard, type PropertyCard } from "../lib/types.ts";
import { Icon } from "./Icon.tsx";
import styles from "./Cards.module.css";

interface Props {
  cards: Card[];
  /** A card's link followed. */
  onOpen(card: Card): void;
  /** A unit's floor plan opened (image or PDF). */
  onPlan?(card: PropertyCard): void;
  /** "Umów prezentację" in the plan viewer; absent: no such button. */
  onLead?(card: PropertyCard): void;
}

export function Cards({ cards, onOpen, onPlan, onLead }: Props) {
  // One viewer per answer, mounted afresh for every opening (`n` is its key).
  const [viewing, setViewing] = useState<{ card: PropertyCard; n: number } | null>(null);
  if (!cards.length) return null;
  const openPlan = (card: PropertyCard) => {
    onPlan?.(card);
    if (card.plan) setViewing((v) => ({ card, n: (v?.n ?? 0) + 1 }));
    else if (card.plan_pdf) openFile(card.plan_pdf);
  };
  return (
    <>
      <div
        class={styles.track}
        data-single={cards.length === 1 || undefined}
        role="list"
        aria-label={isProduct(cards[0]!) ? "Polecane produkty" : "Polecane mieszkania"}
        onClick={(event) => {
          const item = (event.target as Element).closest<HTMLElement>("[data-card]");
          const card = cards.find((c) => c.id === item?.dataset.card);
          if (card && (event.target as Element).closest("a")) onOpen(card);
          interceptLinks(event);
        }}
      >
        {cards.map((card) => (
          <div role="listitem" key={card.id} class={styles.item} data-card={card.id}>
            {isProduct(card) ? <Product card={card} /> : <Property card={card} onPlan={openPlan} />}
          </div>
        ))}
      </div>
      {viewing && <PlanViewer key={viewing.n} card={viewing.card} onLead={onLead} />}
    </>
  );
}

function Thumb({ src, plan = false }: { src: string | null | undefined; plan?: boolean }) {
  return (
    <div class={styles.media} data-plan={plan || undefined}>
      {src && (
        <img
          src={src}
          alt=""
          loading="lazy"
          decoding="async"
          referrerpolicy="no-referrer"
          onError={(e) => ((e.currentTarget as HTMLImageElement).style.visibility = "hidden")}
        />
      )}
    </div>
  );
}

const nameOf = (card: PropertyCard) => card.name ?? card.title;
/** Each fact stays whole ("2. piętro", "65,02 m²"); a line breaks only between them. */
const whole = (text: string) => text.replace(/ /g, " ");
const factsOf = (card: PropertyCard) =>
  [rooms(card.rooms), area(card.area), floor(card.floor)]
    .filter((fact): fact is string => Boolean(fact))
    .map(whole)
    .join(" · ");

/**
 * The whole card opens the unit's page through its title link stretched over
 * it; the plan button sits above that link, so it needs no nesting in <a>.
 */
function Property({ card, onPlan }: { card: PropertyCard; onPlan(card: PropertyCard): void }) {
  const name = nameOf(card);
  const facts = factsOf(card);
  const place = card.investment ?? card.district;
  const thumb = card.images[0];
  return (
    <article class={styles.card} data-link={card.url ? "" : undefined}>
      <div class={styles.mediaWrap}>
        <Thumb src={thumb} plan={Boolean(thumb) && thumb === card.plan} />
        {(card.plan || card.plan_pdf) && (
          <button type="button" class={styles.planButton} onClick={() => onPlan(card)} aria-label={`Rzut: ${name}`}>
            <Icon svg={blueprint} />
            Rzut
          </button>
        )}
      </div>
      <div class={styles.body}>
        <h4 class={styles.title}>
          {card.url ? (
            <a class={styles.stretch} href={card.url} target="_blank" rel="noopener noreferrer">
              {name}
            </a>
          ) : (
            name
          )}
        </h4>
        {place && <p class={styles.place}>{place}</p>}
        {facts && <p class={styles.facts}>{facts}</p>}
        {card.price !== null && <p class={styles.price}>{price(card.price)}</p>}
      </div>
    </article>
  );
}

/**
 * The plan full width, over the chat. A native modal dialog: focus moves in
 * and back, Esc and a tap outside close it, the chat behind is inert.
 */
function PlanViewer({ card, onLead }: { card: PropertyCard; onLead?(card: PropertyCard): void }) {
  const dialog = useRef<HTMLDialogElement>(null);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => dialog.current?.showModal(), []);

  const name = nameOf(card);
  const facts = [card.investment && whole(card.investment), factsOf(card)].filter(Boolean).join(" · ");
  const hide = () => dialog.current?.close();
  return (
    <dialog
      ref={dialog}
      class={styles.viewer}
      aria-label={`Rzut: ${name}`}
      onKeyDown={(event) => {
        // Esc closes the plan only, not the whole chat (App listens on window).
        if (event.key === "Escape") event.stopPropagation();
      }}
      onClick={(event) => {
        // The dialog has no padding of its own: a click on it is a click on the backdrop.
        if (event.target === event.currentTarget) hide();
      }}
    >
      <div class={styles.viewerHead}>
        <div>
          <h2 class={styles.viewerTitle}>{name}</h2>
          {facts && <p class={styles.viewerMeta}>{facts}</p>}
        </div>
        <button type="button" class={styles.viewerClose} onClick={hide} aria-label="Zamknij rzut">
          <Icon svg={close} />
        </button>
      </div>
      <div class={styles.viewerImage} data-loaded={loaded || undefined}>
        <img src={card.plan!} alt={`Rzut: ${name}`} decoding="async" referrerpolicy="no-referrer" onLoad={() => setLoaded(true)} />
      </div>
      <div class={styles.viewerActions}>
        {onLead && (
          <button
            type="button"
            class={styles.primary}
            onClick={() => {
              hide();
              onLead(card);
            }}
          >
            Umów prezentację
          </button>
        )}
        <button type="button" class={styles.ghost} onClick={() => openFile(card.plan_pdf ?? card.plan!)}>
          <Icon svg={card.plan_pdf ? filePdf : external} />
          {card.plan_pdf ? "Karta lokalu (PDF)" : "Pełny rozmiar"}
        </button>
      </div>
    </dialog>
  );
}

function Product({ card }: { card: ProductCard }) {
  return (
    <div class={styles.card}>
      <Thumb src={card.image} />
      <div class={styles.body}>
        <h4 class={styles.title}>{card.name}</h4>
        <p class={styles.meta}>{[card.grade, card.cas_number && `CAS ${card.cas_number}`].filter(Boolean).join(" · ")}</p>
        <p class={styles.price}>{shopPrice(card.price, card.currency)}</p>
        <p class={styles.tags}>
          <span class={card.in_stock ? styles.inStock : styles.outOfStock}>{card.in_stock ? "Dostępny" : "Chwilowo niedostępny"}</span>
          {card.business_only && <span class={styles.tag}>Tylko dla firm</span>}
        </p>
        <div class={styles.actions}>
          {card.url && (
            <a class={styles.primary} href={card.url} target="_blank" rel="noopener noreferrer">
              <Icon svg={external} />
              Zobacz produkt
            </a>
          )}
          {card.add_to_cart_url && card.in_stock && !card.business_only && (
            <a class={styles.secondary} href={card.add_to_cart_url} target="_blank" rel="noopener noreferrer" aria-label={`Dodaj do koszyka: ${card.name}`}>
              <Icon svg={cart} />
            </a>
          )}
          {card.safety_sheet_url && (
            <a class={styles.secondary} href={card.safety_sheet_url} target="_blank" rel="noopener noreferrer" aria-label="Karta charakterystyki" title="Karta charakterystyki">
              <Icon svg={fileText} />
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
